/**
 * PORTFOLIO DEFAULTS
 *
 * Centralized configuration for all portfolio-related default values.
 * This is the SINGLE SOURCE OF TRUTH for default values across all portfolio components.
 *
 * Components should import from here instead of using hardcoded values.
 *
 * @example
 * import { PORTFOLIO_DEFAULTS } from '../../config/portfolioDefaults';
 * const timeline = investmentGoal?.yearsToFreedom || PORTFOLIO_DEFAULTS.timelineYears;
 */

/**
 * Core portfolio defaults used across all components
 */
export const PORTFOLIO_DEFAULTS = Object.freeze({
  // ==========================================================================
  // GOAL DEFAULTS
  // ==========================================================================

  /** Default target passive income per year */
  targetIncome: 80000,

  /** Default PPOR (Primary Place of Residence) mortgage balance */
  pporBalance: 500000,

  /** Default timeline in years to achieve financial freedom */
  timelineYears: 12,

  /** Default starting capital for investment journey */
  startingCapital: 100000,

  /** Default networth target for composite goals */
  networthTarget: 2000000,

  // ==========================================================================
  // STRATEGY DEFAULTS
  // ==========================================================================

  /** Default target gross rental yield (%) */
  targetYield: 5,

  /** Default maximum Loan-to-Value ratio (0.7 = 70%) */
  maxLVR: 0.7,

  /** Default preferred LVR for conservative approach */
  preferredLVR: 0.65,

  /** Maximum concentration per state (%) */
  maxConcentrationPercent: 60,

  /** Default number of properties needed to achieve goal */
  requiredProperties: 7,

  /** Default minimum properties for diversification */
  minProperties: 3,

  /** Default capital growth rate (%) */
  capitalGrowthRate: 5,

  // ==========================================================================
  // PHASE THRESHOLDS
  // ==========================================================================

  /** Ratio of properties at which BUILD phase completes (60%) */
  buildPhaseRatio: 0.6,

  /** Ratio of properties at which OPTIMIZE phase completes (30% more) */
  optimizePhaseRatio: 0.3,

  /** Ratio for HARVEST phase (10% remaining) */
  harvestPhaseRatio: 0.1,

  // ==========================================================================
  // CASH FLOW THRESHOLDS
  // ==========================================================================

  /** Monthly cash flow considered successful ($) */
  cashFlowSuccessThreshold: 500,

  /** Annual cash flow warning threshold ($) */
  cashFlowWarningThreshold: 0,

  // ==========================================================================
  // CONFIDENCE & RISK
  // ==========================================================================

  /** Default confidence percentage when not calculated */
  confidenceDefault: 75,

  /** High confidence threshold (%) */
  confidenceHigh: 80,

  /** Medium confidence threshold (%) */
  confidenceMedium: 60,

  /** Default risk profile */
  riskProfile: 'balanced',

  // ==========================================================================
  // GOAL PRESETS (For Goal Selection UI)
  // ==========================================================================

  /** PPOR payoff amount presets */
  pporPresets: Object.freeze([300000, 500000, 750000, 1000000]),

  /** Passive income target presets */
  incomePresets: Object.freeze([50000, 80000, 120000, 200000]),

  /** True freedom (income + networth) presets */
  freedomPresets: Object.freeze([80000, 120000, 180000, 250000]),

  /** Starting capital presets */
  capitalPresets: Object.freeze([50000, 100000, 200000, 500000]),

  // ==========================================================================
  // PROPERTY DEFAULTS
  // ==========================================================================

  /** Default property value for new properties */
  defaultPropertyValue: 600000,

  /** Default weekly rent */
  defaultWeeklyRent: 500,

  /** Default deposit percentage (20%) */
  defaultDepositPercent: 0.2,

  // ==========================================================================
  // UI DEFAULTS
  // ==========================================================================

  /** Default debt-free property count at end of journey */
  debtFreeCount: 3,

  /** Default number of properties in final portfolio */
  finalPropertyCount: 5,
});

/**
 * Default portfolio strategy structure
 * Used when no strategy is set or for initialization
 */
export const DEFAULT_PORTFOLIO_STRATEGY = Object.freeze({
  targetReturns: {
    grossYield: PORTFOLIO_DEFAULTS.targetYield,
    capitalGrowth: PORTFOLIO_DEFAULTS.capitalGrowthRate,
    totalReturn: PORTFOLIO_DEFAULTS.targetYield + PORTFOLIO_DEFAULTS.capitalGrowthRate,
  },
  gearing: {
    maxLVR: PORTFOLIO_DEFAULTS.maxLVR,
    preferredLVR: PORTFOLIO_DEFAULTS.preferredLVR,
    gearingStrategy: 'Moderate',
  },
  diversificationRules: {
    maxPercentPerState: PORTFOLIO_DEFAULTS.maxConcentrationPercent,
    maxPercentPerSuburb: 25,
    minNumberOfProperties: PORTFOLIO_DEFAULTS.minProperties,
  },
  dealSizeRange: {
    minimum: 400000,
    maximum: 1500000,
    preferred: 800000,
  },
});

/**
 * Default investment goal structure
 * Used when no goal is set
 */
export const DEFAULT_INVESTMENT_GOAL = Object.freeze({
  goalType: null,
  targetIncome: PORTFOLIO_DEFAULTS.targetIncome,
  pporBalance: 0,
  networthTarget: 0,
  freedomYear: null,
  yearsToFreedom: PORTFOLIO_DEFAULTS.timelineYears,
  passiveIncome: 0,
  achievedNetworth: 0,
  initialCapital: PORTFOLIO_DEFAULTS.startingCapital,
  timelineEvents: [],
  portfolio: [],
  optimalStrategy: null,
  confidenceLevel: null,
  scenarioAnalysis: null,
  portfolioSnapshot: null,
  completedAt: null,
});

export default PORTFOLIO_DEFAULTS;
